import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material'
import React from 'react'
import * as yup from 'yup'
import { mainTheme } from '../../../styles/mainTheme'
import ReactInputMask from 'react-input-mask'
import { useFormik } from 'formik'
import { isValidCPF } from '@brazilian-utils/brazilian-utils'
import { toast } from 'react-toastify'
import { uploadAllowedCustomerBatchTemplate } from '../../../services/campaign'
import { StyledTextFieldSquare } from '../../../utils/sharedStyles'
import { useSelector } from '../../../state/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../../state'
import { ActionsContainerGrid } from './styles'

export interface AllowedCustomerFormData {
  fullName?: string
  document?: string
  email?: string
  givenVoucherAmount?: number
}

const schema = yup.object().shape({
  fullName: yup.string(),
  document: yup.string().required('Campo obrigatório'),
  email: yup.string().email('Esse campo deve conter um email válido'),
  givenVoucherAmount: yup.number().required('Campo obrigatório'),
})

const AddSingleCustomerModal = (props: {
  open: boolean
  setOpen: (value: boolean) => void
  loading: boolean
  setLoading: (value: boolean) => void
}) => {
  const { open, setOpen, loading, setLoading } = props
  const campaign = useSelector((state) => state.campaigns.campaignDetails)
  const dispatch = useDispatch()

  const parseFormData = () => {
    const data = {
      'Nome Completo': values.fullName,
      Email: values.email,
      CPF: values.document?.replace(/\D/g, ''),
      'Quantidade de Vale Vacina': values.givenVoucherAmount,
    }
    const csvRows = []
    const headers = Object.keys(data)
    csvRows.push(headers.join(','))
    const csvValues = Object.values(data).join(',')
    csvRows.push(csvValues)
    const csv = csvRows.join('\n')
    const formData = new FormData()
    formData.append('file', new Blob([csv]), 'template.csv')
    formData.append('type', 'file')
    return formData
  }

  const createAllowedCustomer = async () => {
    if (!isValidCPF(values.document!)) {
      setErrors({
        ...errors,
        document: 'CPF inválido',
      })
      return
    }

    if (values.fullName?.charAt(0) === ' ') {
      setErrors({
        ...errors,
        fullName: 'Nome inválido',
      })
      return
    }
    try {
      setLoading(true)
      const formData = parseFormData()
      await uploadAllowedCustomerBatchTemplate(formData, String(campaign?._id))
      resetForm()
      dispatch(
        actions.vouchers.getVouchersFetch({
          campaignId: String(campaign?._id),
        })
      )
    } catch (error) {
      toast(String(error), { type: 'error' })
      console.error(error)
    } finally {
      setOpen(false)
      setLoading(false)
    }
  }

  const { values, setFieldValue, handleSubmit, errors, setErrors, resetForm } =
    useFormik<AllowedCustomerFormData>({
      initialValues: {},
      onSubmit: createAllowedCustomer,
      validationSchema: schema,
      validateOnChange: false,
    })

  const shouldDisableConfirmButton = () => {
    return !values.givenVoucherAmount || !values.document || loading
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Adicionar beneficiário</DialogTitle>
      <DialogContent>
        <Typography
          style={{ marginBottom: mainTheme.spacing(2) }}
          variant="body1"
        >
          Preencha as informações abaixo e confirme para incluir um beneficiário
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} xs={12}>
            <Grid item xs={6}>
              <StyledTextFieldSquare
                fullWidth
                label="Nome"
                variant="outlined"
                value={values?.fullName}
                onChange={(ev) => {
                  setFieldValue('fullName', ev.target.value.replace(/\d/g, ''))
                  setErrors({
                    ...errors,
                    fullName: '',
                  })
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.fullName)}
                inputProps={{
                  'data-testid': 'fullName-input',
                }}
              />
              {errors.fullName && (
                <Typography variant="body2" color="error">
                  {errors.fullName}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <ReactInputMask
                mask="999.999.999-99"
                disabled={false}
                value={values.document}
                onChange={(ev) => {
                  setFieldValue('document', ev.target.value)
                  setErrors({
                    ...errors,
                    document: '',
                  })
                }}
              >
                {() => (
                  <StyledTextFieldSquare
                    size="small"
                    variant="outlined"
                    label="CPF*"
                    error={Boolean(errors.document)}
                    inputProps={{
                      'data-testid': 'document-input',
                    }}
                  />
                )}
              </ReactInputMask>
              {errors.document && (
                <Typography variant="body2" color="error">
                  {errors.document}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid
            style={{ marginTop: mainTheme.spacing(1) }}
            container
            spacing={2}
            xs={12}
          >
            <Grid item xs={6}>
              <StyledTextFieldSquare
                type="email"
                fullWidth
                label="E-mail"
                variant="outlined"
                value={values?.email}
                onChange={(ev) => {
                  setFieldValue('email', ev.target.value)
                  setErrors({
                    ...errors,
                    email: '',
                  })
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.email)}
                inputProps={{
                  'data-testid': 'email-input',
                }}
              />
              {errors.email && (
                <Typography variant="body2" color="error">
                  {errors.email}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <StyledTextFieldSquare
                type="number"
                fullWidth
                label="Quantidade de vales*"
                variant="outlined"
                value={values?.givenVoucherAmount}
                onChange={(ev) => {
                  setFieldValue('givenVoucherAmount', parseInt(ev.target.value))
                  setErrors({
                    ...errors,
                    givenVoucherAmount: '',
                  })
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.givenVoucherAmount)}
                inputProps={{
                  'data-testid': 'givenVoucherAmount-input',
                  min: 1,
                }}
              />
              {errors.givenVoucherAmount && (
                <Typography variant="body2" color="error">
                  {errors.givenVoucherAmount}
                </Typography>
              )}
            </Grid>
          </Grid>
          <ActionsContainerGrid
            style={{
              marginTop: mainTheme.spacing(1),
              marginBottom: mainTheme.spacing(1),
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            container
            spacing={2}
            xs={12}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpen(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              style={{ marginLeft: mainTheme.spacing(1) }}
              variant="contained"
              type="submit"
              color="primary"
              disabled={shouldDisableConfirmButton()}
            >
              Confirmar
            </Button>
          </ActionsContainerGrid>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddSingleCustomerModal
